/**
 * SuspensionesEditor — CRUD de suspensiones puntuales de la sala
 * (feriados, capacitaciones, mantenimiento).
 *
 * Datos: subcolección `salas/{salaId}/suspensiones`.
 */
import { useState, useEffect } from 'react';
import { collection, onSnapshot, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Plus, Trash2 } from 'lucide-react';
import { db, functions } from '../../config/firebase';
import { Input } from '../ui/Input';
import { useToast } from '../../contexts/ToastContext';
import styles from './SuspensionesEditor.module.css';

type TipoSuspension = 'dia_completo' | 'rango_horas' | 'multi_dia';

interface Suspension {
  id: string;
  tipo: TipoSuspension;
  fecha?: string;
  horaInicio?: string;
  horaFin?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  motivo: string;
  creadoEn?: Timestamp;
}

const callCrear = httpsCallable(functions, 'portal_crearSuspensionSala');
const callEliminar = httpsCallable(functions, 'portal_eliminarSuspensionSala');

export interface SuspensionesEditorProps {
  salaId: string;
}

export function SuspensionesEditor({ salaId }: SuspensionesEditorProps) {
  const { addToast } = useToast();
  const [items, setItems] = useState<Suspension[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    tipo: 'dia_completo' as TipoSuspension,
    fecha: new Date().toISOString().slice(0, 10),
    horaInicio: '12:00',
    horaFin: '14:00',
    fechaDesde: new Date().toISOString().slice(0, 10),
    fechaHasta: new Date().toISOString().slice(0, 10),
    motivo: '',
  });

  useEffect(() => {
    if (!salaId) return;
    const ref = collection(db, `salas/${salaId}/suspensiones`);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const list: Suspension[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<Suspension, 'id'>),
        }));
        // Sort por fecha desc
        list.sort((a, b) => {
          const fa = a.fecha ?? a.fechaDesde ?? '';
          const fb = b.fecha ?? b.fechaDesde ?? '';
          return fb.localeCompare(fa);
        });
        setItems(list);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsub();
  }, [salaId]);

  const resetForm = () => {
    setForm({
      tipo: 'dia_completo',
      fecha: new Date().toISOString().slice(0, 10),
      horaInicio: '12:00',
      horaFin: '14:00',
      fechaDesde: new Date().toISOString().slice(0, 10),
      fechaHasta: new Date().toISOString().slice(0, 10),
      motivo: '',
    });
  };

  // No usamos <form> porque el componente puede vivir dentro de otro
  // form (SalaFormPage). Forms anidados disparan el submit del padre.
  const handleSubmit = async () => {
    if (form.motivo.trim().length < 3) {
      addToast('error', 'Motivo requerido (mín. 3 caracteres)');
      return;
    }
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        salaId,
        tipo: form.tipo,
        motivo: form.motivo,
      };
      if (form.tipo === 'dia_completo') payload.fecha = form.fecha;
      if (form.tipo === 'rango_horas') {
        payload.fecha = form.fecha;
        payload.horaInicio = form.horaInicio;
        payload.horaFin = form.horaFin;
      }
      if (form.tipo === 'multi_dia') {
        payload.fechaDesde = form.fechaDesde;
        payload.fechaHasta = form.fechaHasta;
      }
      await callCrear(payload);
      addToast('success', 'Suspensión registrada');
      resetForm();
      setShowForm(false);
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (suspensionId: string) => {
    try {
      await callEliminar({ salaId, suspensionId });
      addToast('success', 'Suspensión eliminada');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Error');
    }
  };

  const formatSuspension = (s: Suspension): string => {
    if (s.tipo === 'dia_completo') return `${s.fecha} — día completo`;
    if (s.tipo === 'rango_horas') {
      return `${s.fecha} — ${s.horaInicio} a ${s.horaFin}`;
    }
    return `${s.fechaDesde} → ${s.fechaHasta}`;
  };

  return (
    <div className={styles.editor}>
      <div className={styles.toolbar}>
        <button
          type="button"
          className={styles.addBtn}
          onClick={() => setShowForm((v) => !v)}
        >
          <Plus size={14} /> {showForm ? 'Cancelar' : 'Agregar'}
        </button>
      </div>

      {showForm && (
        <div className={styles.formCard}>
          <div className={styles.tipoSelector}>
            {[
              { value: 'dia_completo', label: 'Día completo' },
              { value: 'rango_horas', label: 'Rango horario' },
              { value: 'multi_dia', label: 'Varios días' },
            ].map((opt) => (
              <label key={opt.value} className={styles.tipoOption}>
                <input
                  type="radio"
                  name="tipo"
                  value={opt.value}
                  checked={form.tipo === opt.value}
                  onChange={() =>
                    setForm({ ...form, tipo: opt.value as TipoSuspension })
                  }
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>

          <div className={styles.formGrid}>
            {form.tipo === 'dia_completo' && (
              <Input
                label="Fecha *"
                type="date"
                value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                required
              />
            )}
            {form.tipo === 'rango_horas' && (
              <>
                <Input
                  label="Fecha *"
                  type="date"
                  value={form.fecha}
                  onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                  required
                />
                <Input
                  label="Hora inicio *"
                  type="time"
                  value={form.horaInicio}
                  onChange={(e) => setForm({ ...form, horaInicio: e.target.value })}
                  required
                />
                <Input
                  label="Hora fin *"
                  type="time"
                  value={form.horaFin}
                  onChange={(e) => setForm({ ...form, horaFin: e.target.value })}
                  required
                />
              </>
            )}
            {form.tipo === 'multi_dia' && (
              <>
                <Input
                  label="Desde *"
                  type="date"
                  value={form.fechaDesde}
                  onChange={(e) => setForm({ ...form, fechaDesde: e.target.value })}
                  required
                />
                <Input
                  label="Hasta *"
                  type="date"
                  value={form.fechaHasta}
                  onChange={(e) => setForm({ ...form, fechaHasta: e.target.value })}
                  required
                />
              </>
            )}
            <Input
              label="Motivo *"
              placeholder="Ej. Feriado, Capacitación"
              value={form.motivo}
              onChange={(e) => setForm({ ...form, motivo: e.target.value })}
              required
            />
          </div>

          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.btnPri}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Guardando…' : 'Registrar suspensión'}
            </button>
          </div>
        </div>
      )}

      <div className={styles.list}>
        {loading ? (
          <p className={styles.empty}>Cargando…</p>
        ) : items.length === 0 ? (
          <p className={styles.empty}>Sin suspensiones registradas.</p>
        ) : (
          items.map((s) => (
            <div key={s.id} className={styles.item}>
              <div className={styles.itemInfo}>
                <span className={styles.itemFecha}>{formatSuspension(s)}</span>
                <span className={styles.itemMotivo}>{s.motivo}</span>
              </div>
              <button
                type="button"
                className={styles.delBtn}
                onClick={() => handleDelete(s.id)}
                title="Eliminar"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
